'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  Car,
  User,
  Phone,
  FileText,
  Printer,
  MessageCircle,
  Camera,
  Trash2,
  CheckCircle2,
  ArrowRight,
  Fuel,
  Sparkles,
  FileCheck,
  LifeBuoy,
  Gauge,
  X,
  AlertTriangle,
  IdCard,
} from 'lucide-react';
import type { Booking, ReturnCharge, Vehicle, RentalClient } from '@/types';
import { api } from '@/lib/api';
import type { AgencyProfile } from '@/lib/agency';
import { buildWhatsAppLink, buildBookingWhatsAppMessage } from '@/lib/morocco';
import { formatMoney } from '@/lib/currency';
import { dirhams } from '@/lib/booking/money';
import { SlideOver } from './SlideOver';
import { StatusChip, bookingTone, useDeskToast } from './desk-feedback';

interface HandoverModuleProps {
  bookings: Booking[];
  vehicles: Vehicle[];
  clients: RentalClient[];
  onUpdateBooking: (b: Booking) => void | Promise<boolean>;
  onUpdateVehicle: (v: Vehicle) => void;
  agency?: AgencyProfile | null;
  currency?: string | null;
}

type Checklist = {
  exteriorClean: boolean;
  documentsHanded: boolean;
  spareTire: boolean;
};

const DEFAULT_CHECKLIST: Checklist = {
  exteriorClean: true,
  documentsHanded: false,
  spareTire: true,
};

const FUEL_LEVELS = [
  { value: '', label: '—' },
  { value: 'empty', label: 'Vide' },
  { value: 'quarter', label: '1/4' },
  { value: 'half', label: '1/2' },
  { value: 'three_quarters', label: '3/4' },
  { value: 'full', label: 'Plein' },
] as const;

const CAUTION_STATUSES = [
  { value: 'held', label: 'Caution détenue' },
  { value: 'returned', label: 'Caution rendue' },
  { value: 'partial', label: 'Caution retenue en partie' },
  { value: 'retained', label: 'Caution retenue' },
] as const;

function fuelLabel(value: string): string {
  return FUEL_LEVELS.find((level) => level.value === value)?.label ?? '—';
}

function parseMileage(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
}

const NEXT_STATUS: Record<string, Booking['status'] | null> = {
  pending: 'confirmed',
  confirmed: 'active',
  active: 'returned',
  returned: 'completed',
  completed: null,
  cancelled: null,
};

const NEXT_LABEL: Record<string, string> = {
  pending: 'Confirmer',
  confirmed: 'Check-out → Active',
  active: 'Check-in → Returned',
  returned: 'Clôturer → Completed',
};

export function HandoverModule({ bookings, vehicles, clients, onUpdateBooking, onUpdateVehicle, agency, currency }: HandoverModuleProps) {
  const cur = currency ?? 'MAD';
  const open = useMemo(
    () => bookings.filter((b) => ['pending', 'confirmed', 'active'].includes(b.status)),
    [bookings]
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Booking | null>(null);
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    api<Booking>(`/api/bookings/${selectedId}`)
      .then((row) => { if (!cancelled) setDetail(row); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId]);
  const notify = useDeskToast();
  const selected = (detail?.id === selectedId ? detail : null) ?? bookings.find((b) => b.id === selectedId) ?? null;

  const vehicle = selected ? vehicles.find((v) => v.id === selected.vehicleId) ?? null : null;
  const matchedClient = selected
    ? clients.find((c) => c.phone.replace(/\D/g, '').slice(-9) === selected.customerPhone.replace(/\D/g, '').slice(-9))
    : null;

  const [checklist, setChecklist] = useState<Checklist>(DEFAULT_CHECKLIST);
  const [pickupKm, setPickupKm] = useState<string>('');
  const [returnKm, setReturnKm] = useState<string>('');
  const [pickupFuel, setPickupFuel] = useState('');
  const [returnFuel, setReturnFuel] = useState('');
  const [charges, setCharges] = useState<ReturnCharge[]>([]);
  const [chargeLabel, setChargeLabel] = useState('Carburant');
  const [chargeAmount, setChargeAmount] = useState('');
  const [cautionStatus, setCautionStatus] = useState<NonNullable<Booking['cautionStatus']>>('held');
  const [cautionRetainedInput, setCautionRetainedInput] = useState('');
  const [showContract, setShowContract] = useState(false);
  const [draftBookingId, setDraftBookingId] = useState<string | null>(null);

  const activeBooking = selected;
  const activeVehicle = vehicle;
  const selectedBookingId = activeBooking?.id ?? null;
  if (selectedBookingId !== draftBookingId) {
    setDraftBookingId(selectedBookingId);
    setPickupKm(activeBooking?.pickupMileage != null
      ? String(activeBooking.pickupMileage)
      : activeVehicle ? String(activeVehicle.mileage) : '');
    setReturnKm(activeBooking?.returnMileage != null ? String(activeBooking.returnMileage) : '');
    setPickupFuel(activeBooking?.pickupFuel ?? '');
    setReturnFuel(activeBooking?.returnFuel ?? '');
    setCharges(activeBooking?.returnCharges ?? []);
    setCautionStatus(activeBooking?.cautionStatus ?? 'held');
    setCautionRetainedInput(activeBooking?.cautionRetained ? String(activeBooking.cautionRetained) : '');
    setChargeLabel('Carburant');
    setChargeAmount('');
  }

  const totalPhotos = activeBooking
    ? (activeBooking.pickupPhotos?.length ?? 0) + (activeBooking.returnPhotos?.length ?? 0) + (activeBooking.idDocumentPhoto ? 1 : 0)
    : 0;

  const chargeTotal = dirhams(charges.reduce((sum, line) => sum + line.amount, 0));
  const reste = activeBooking
    ? dirhams(activeBooking.totalPrice + chargeTotal - (activeBooking.avanceAmount ?? 0))
    : 0;

  function retainedAmount(booking: Booking): number {
    if (cautionStatus === 'retained') return dirhams(booking.cautionAmount ?? 0);
    if (cautionStatus === 'partial') return dirhams(Number(cautionRetainedInput) || 0);
    return 0;
  }

  function withInspection(booking: Booking): Booking {
    return {
      ...booking,
      pickupMileage: parseMileage(pickupKm),
      returnMileage: parseMileage(returnKm),
      pickupFuel,
      returnFuel,
      returnCharges: charges.map((line) => ({ label: line.label, amount: dirhams(line.amount) })),
      cautionStatus,
      cautionRetained: retainedAmount(booking),
    };
  }

  function readFiles(files: FileList | null, cb: (urls: string[]) => void) {
    if (!files) return;
    const readers = Array.from(files).map(
      (f) =>
        new Promise<string>((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.readAsDataURL(f);
        })
    );
    Promise.all(readers).then(cb);
  }

  function addPickupPhotos(files: FileList | null) {
    if (!activeBooking) return;
    readFiles(files, (urls) =>
      onUpdateBooking({ ...activeBooking, pickupPhotos: [...(activeBooking.pickupPhotos ?? []), ...urls] })
    );
  }
  function addReturnPhotos(files: FileList | null) {
    if (!activeBooking) return;
    readFiles(files, (urls) =>
      onUpdateBooking({ ...activeBooking, returnPhotos: [...(activeBooking.returnPhotos ?? []), ...urls] })
    );
  }
  function setIdPhoto(files: FileList | null) {
    if (!activeBooking) return;
    readFiles(files, (urls) => {
      if (urls[0]) onUpdateBooking({ ...activeBooking, idDocumentPhoto: urls[0] });
    });
  }

  function removePhoto(kind: 'pickup' | 'return' | 'id', idx?: number) {
    if (!activeBooking) return;
    if (kind === 'pickup')
      onUpdateBooking({ ...activeBooking, pickupPhotos: (activeBooking.pickupPhotos ?? []).filter((_, i) => i !== idx) });
    if (kind === 'return')
      onUpdateBooking({ ...activeBooking, returnPhotos: (activeBooking.returnPhotos ?? []).filter((_, i) => i !== idx) });
    if (kind === 'id') onUpdateBooking({ ...activeBooking, idDocumentPhoto: undefined });
  }

  async function advanceStatus() {
    if (!activeBooking) return;
    const next = NEXT_STATUS[activeBooking.status];
    if (!next) return;
    const saved = await Promise.resolve(onUpdateBooking(withInspection({ ...activeBooking, status: next })));
    if (saved === false) return;
    if (activeVehicle) {
      const current = await api<Vehicle>(`/api/vehicles/${activeVehicle.id}`);
      if (activeBooking.status === 'active' && next === 'returned') {
        const km = parseMileage(returnKm);
        if (km !== null && km > 0) onUpdateVehicle({ ...current, mileage: km, status: 'available' });
      }
      if (activeBooking.status === 'confirmed') {
        onUpdateVehicle({ ...current, status: 'rented' });
      }
    }
    notify(next === 'returned' || next === 'completed' ? 'Handover saved.' : 'Status saved.');
    if (next === 'returned' || next === 'completed') setSelectedId(null);
  }

  async function saveInspection() {
    if (!activeBooking) return;
    const saved = await Promise.resolve(onUpdateBooking(withInspection(activeBooking)));
    if (saved === false) return;
    notify('Inspection saved.');
  }

  function addCharge() {
    const amount = dirhams(Number(chargeAmount));
    const label = chargeLabel.trim();
    if (label === '' || !Number.isFinite(amount) || amount < 0) return;
    setCharges((current) => [...current, { label, amount }]);
    setChargeAmount('');
  }

  const waLink = activeBooking
    ? buildWhatsAppLink(activeBooking.customerPhone, buildBookingWhatsAppMessage(activeBooking, agency?.shortName ?? 'fleetava Agadir', agency?.currency ?? cur))
    : '';

  return (
    <div>
      {/* Left: open bookings */}
      <div className="min-w-0 rounded-3xl border border-line bg-card p-3">
        <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">
          Dossiers ouverts ({open.length})
        </p>
        <div className="max-h-64 space-y-2 overflow-y-auto lg:max-h-none lg:overflow-visible">
          {open.map((b) => (
            <button
              key={b.id}
              onClick={() => setSelectedId(b.id)}
              className={`w-full rounded-2xl border p-3 text-left transition ${
                b.id === selectedId
                  ? 'border-ink bg-ink text-shell'
                  : 'border-line bg-shell hover:border-ink'
              }`}
            >
              <div className="flex min-w-0 items-center justify-between gap-2">
                <p className="truncate text-xs font-bold" title={b.id}>{b.id}</p>
                <StatusChip tone={bookingTone(b.status)}>{b.status}</StatusChip>
              </div>
              <p className={`mt-1 truncate text-xs ${b.id === selectedId ? 'text-shell/75' : 'text-muted'}`} title={`${b.customerName} · ${b.vehicleName}`}>
                {b.customerName} · {b.vehicleName}
              </p>
              <p className={`text-[11px] ${b.id === selectedId ? 'text-shell/60' : 'text-muted'}`}>
                {b.pickupDate} → {b.dropoffDate}
              </p>
            </button>
          ))}
          {open.length === 0 && <p className="p-3 text-xs text-muted">Aucune réservation ouverte. Les dossiers pending, confirmed et active apparaîtront ici.</p>}
        </div>
      </div>

      {activeBooking && (
      <SlideOver
        open
        title={`${activeBooking.customerName} · ${activeBooking.vehicleName}`}
        onClose={() => { setSelectedId(null); setShowContract(false); }}
      >
      <div className="space-y-4">
        {/* Client + vehicle + dates */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="min-w-0 rounded-3xl border border-line bg-card p-4">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
              <User size={14} /> Client
            </p>
            <p className="mt-2 truncate text-sm font-bold text-ink" title={activeBooking.customerName}>{activeBooking.customerName}</p>
            <p className="flex items-center gap-1 truncate text-xs text-muted" title={`${activeBooking.customerPhone} · ${activeBooking.customerEmail}`}>
              <Phone size={12} className="shrink-0" /> <span className="truncate">{activeBooking.customerPhone} · {activeBooking.customerEmail}</span>
            </p>
            <p className="mt-1 text-xs text-muted">Permis : {activeBooking.driverLicense || '—'}</p>
            <p className="text-xs text-muted">
              CIN / Passeport : {matchedClient?.cinPassport ?? <span className="text-muted">non retrouvé (clients)</span>}
            </p>
            {matchedClient && (
              <p className="mt-1 inline-block rounded-full bg-shell px-2 py-0.5 text-[11px] text-muted">
                Fiche client : {matchedClient.rentalsCount} locations · {formatMoney(matchedClient.totalSpent, cur)}
              </p>
            )}
          </div>
          <div className="min-w-0 rounded-3xl border border-line bg-card p-4">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
              <Car size={14} /> Véhicule
            </p>
            <p className="mt-2 truncate text-sm font-bold text-ink" title={activeBooking.vehicleName}>
              {activeBooking.vehicleName}{' '}
              {activeVehicle && <span className="text-xs font-medium text-muted">· Matricule {activeVehicle.licensePlate}</span>}
            </p>
            {activeVehicle && (
              <p className="text-xs text-muted">
                {activeVehicle.brand} {activeVehicle.year} · {activeVehicle.transmission} · {activeVehicle.fuelType} ·{' '}
                {activeVehicle.mileage.toLocaleString('fr-MA')} km
              </p>
            )}
            <p className="mt-2 text-xs text-muted">
              Retrait : {activeBooking.pickupLocation} — {activeBooking.pickupDate} à {activeBooking.pickupTime}
            </p>
            <p className="text-xs text-muted">
              Retour : {activeBooking.dropoffLocation} — {activeBooking.dropoffDate} à {activeBooking.dropoffTime}
            </p>
          </div>
        </div>

        {/* Financial table */}
        <div className="min-w-0 rounded-3xl border border-line bg-card p-4">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
            <FileText size={14} /> Financier — Contrat de location
          </p>
          <div className="mt-3 overflow-x-auto rounded-2xl border border-line">
            <table className="w-full min-w-[420px] text-xs tabular-nums">
              <tbody>
                <tr className="border-b border-line">
                  <td className="bg-shell px-3 py-2 text-muted">Total</td>
                  <td className="px-3 py-2 text-right font-bold">{formatMoney(activeBooking.totalPrice, cur)}</td>
                </tr>
                <tr className="border-b border-line">
                  <td className="bg-shell px-3 py-2 text-muted">Avance</td>
                  <td className="px-3 py-2 text-right font-bold">{formatMoney(activeBooking.avanceAmount ?? 0, cur)}</td>
                </tr>
                {chargeTotal > 0 && (
                  <tr className="border-b border-line">
                    <td className="bg-shell px-3 py-2 text-muted">Frais de retour</td>
                    <td className="px-3 py-2 text-right font-bold">{formatMoney(chargeTotal, cur)}</td>
                  </tr>
                )}
                <tr className="border-b border-line">
                  <td className="bg-shell px-3 py-2 font-semibold text-ink">Reste à payer</td>
                  <td className="px-3 py-2 text-right font-bold text-[#FF8A00]">{formatMoney(reste, cur)}</td>
                </tr>
                <tr>
                  <td className="bg-shell px-3 py-2 text-muted">
                    Caution {activeBooking.cautionType ? `(${activeBooking.cautionType})` : ''}
                  </td>
                  <td className="px-3 py-2 text-right font-bold">{formatMoney(activeBooking.cautionAmount ?? 0, cur)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Checklist + mileage */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-3xl border border-line bg-card p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-muted">Checklist remise</p>
            <label className="mt-2 flex items-center gap-2 text-xs text-ink">
              <Fuel size={14} /> Carburant sortie
              <select
                value={pickupFuel}
                onChange={(e) => setPickupFuel(e.target.value)}
                className="ml-auto rounded-xl border border-line bg-shell px-2 py-1 text-xs"
              >
                {FUEL_LEVELS.map((level) => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </label>
            <label className="mt-2 flex items-center gap-2 text-xs text-ink">
              <Fuel size={14} /> Carburant retour
              <select
                value={returnFuel}
                onChange={(e) => setReturnFuel(e.target.value)}
                className="ml-auto rounded-xl border border-line bg-shell px-2 py-1 text-xs"
              >
                {FUEL_LEVELS.map((level) => (
                  <option key={`return-${level.value}`} value={level.value}>{level.label}</option>
                ))}
              </select>
            </label>
            {(
              [
                ['exteriorClean', 'Extérieur propre', Sparkles],
                ['documentsHanded', 'Documents remis (assurance + VT)', FileCheck],
                ['spareTire', 'Roue de secours + cric', LifeBuoy],
              ] as const
            ).map(([key, label, Icon]) => (
              <label key={key} className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={checklist[key]}
                  onChange={(e) => setChecklist({ ...checklist, [key]: e.target.checked })}
                  className="h-4 w-4 rounded accent-brand"
                />
                <Icon size={14} className="text-muted" /> {label}
              </label>
            ))}
          </div>
          <div className="rounded-3xl border border-line bg-card p-4">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
              <Gauge size={14} /> Kilométrage
            </p>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="text-xs text-muted">
                Sortie (pickup)
                <input
                  type="number"
                  value={pickupKm}
                  onChange={(e) => setPickupKm(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
                />
              </label>
              <label className="text-xs text-muted">
                Retour (return)
                <input
                  type="number"
                  value={returnKm}
                  onChange={(e) => setReturnKm(e.target.value)}
                  placeholder="km au retour"
                  className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
                />
              </label>
            </div>
            <p className="mt-1 text-[11px] text-muted">Au retour, le kilométrage véhicule est mis à jour. Le prix signé ne change pas.</p>
          </div>
        </div>

        <div className="rounded-3xl border border-line bg-card p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-muted">Frais de retour</p>
          <p className="mt-1 text-[11px] text-muted">Ces lignes s&apos;ajoutent au reste. Elles ne modifient pas le total signé. La caution n&apos;est pas un encaissement.</p>
          <ul className="mt-2 space-y-1">
            {charges.map((line, index) => (
              <li key={`${line.label}-${index}`} className="flex items-center justify-between gap-2 text-xs text-ink">
                <span className="truncate">{line.label}</span>
                <span className="flex items-center gap-2">
                  {formatMoney(line.amount, cur)}
                  <button type="button" onClick={() => setCharges((current) => current.filter((_, item) => item !== index))} className="text-danger" aria-label={`Retirer ${line.label}`}>
                    <Trash2 size={12} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <label className="text-xs text-muted">
              Libellé
              <input
                value={chargeLabel}
                onChange={(event) => setChargeLabel(event.target.value)}
                className="mt-1 block w-36 rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
              />
            </label>
            <label className="text-xs text-muted">
              Montant
              <input
                type="number"
                min="0"
                step="0.01"
                value={chargeAmount}
                onChange={(event) => setChargeAmount(event.target.value)}
                className="mt-1 block w-28 rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
              />
            </label>
            <button type="button" onClick={addCharge} className="rounded-xl border border-line px-3 py-2 text-xs font-semibold text-ink">
              Ajouter
            </button>
          </div>
          <label className="mt-3 block text-xs text-muted">
            Caution
            <select
              value={cautionStatus}
              onChange={(event) => setCautionStatus(event.target.value as NonNullable<Booking['cautionStatus']>)}
              className="mt-1 block rounded-xl border border-line bg-shell px-2 py-2 text-xs text-ink"
            >
              {CAUTION_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </label>
          {cautionStatus === 'partial' && (
            <label className="mt-2 block text-xs text-muted">
              Montant retenu
              <input
                type="number"
                min="0"
                step="0.01"
                value={cautionRetainedInput}
                onChange={(event) => setCautionRetainedInput(event.target.value)}
                className="mt-1 block w-28 rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
              />
            </label>
          )}
        </div>

        {/* Photos */}
        <div className="rounded-3xl border border-line bg-card p-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
              <Camera size={14} /> Photos ({totalPhotos})
            </p>
            {totalPhotos > 10 && (
              <p className="flex items-center gap-1 text-[11px] font-medium text-warning">
                <AlertTriangle size={12} /> +10 photos : risque de dépasser la limite localStorage.
              </p>
            )}
          </div>
          <PhotoSection
            title="Photos de sortie (pickup)"
            photos={activeBooking.pickupPhotos ?? []}
            multiple
            onAdd={addPickupPhotos}
            onRemove={(i) => removePhoto('pickup', i)}
          />
          <PhotoSection
            title="Photos de retour (return)"
            photos={activeBooking.returnPhotos ?? []}
            multiple
            onAdd={addReturnPhotos}
            onRemove={(i) => removePhoto('return', i)}
          />
          <div className="mt-3">
            <p className="flex items-center gap-1 text-xs font-semibold text-ink">
              <IdCard size={14} /> Pièce d&apos;identité (1 photo)
            </p>
            <label className="mt-2 inline-block cursor-pointer rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-shell">
              {activeBooking.idDocumentPhoto ? 'Remplacer' : 'Ajouter'} la pièce
              <input type="file" accept="image/*" className="hidden" onChange={(e) => setIdPhoto(e.target.files)} />
            </label>
            {activeBooking.idDocumentPhoto && (
              <div className="relative mt-2 inline-block">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={activeBooking.idDocumentPhoto} alt="ID" className="h-24 w-36 rounded-xl border border-line object-cover" />
                <button
                  onClick={() => removePhoto('id')}
                  className="absolute -right-2 -top-2 rounded-full bg-ink p-1 text-shell"
                  aria-label="Supprimer"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={saveInspection}
            className="flex items-center gap-2 rounded-2xl border border-line bg-card px-4 py-2.5 text-xs font-bold text-ink hover:bg-shell"
          >
            Enregistrer l&apos;état
          </button>
          {NEXT_STATUS[activeBooking.status] && (
            <button
              onClick={advanceStatus}
              className="flex items-center gap-2 rounded-2xl bg-ink px-4 py-2.5 text-xs font-bold text-shell hover:brightness-110"
            >
              <CheckCircle2 size={14} /> {NEXT_LABEL[activeBooking.status]} <ArrowRight size={14} />
            </button>
          )}
          <button
            onClick={() => setShowContract(true)}
            className="flex items-center gap-2 rounded-2xl border border-line bg-card px-4 py-2.5 text-xs font-bold text-ink hover:bg-shell"
          >
            <Printer size={14} /> Imprimer le contrat
          </button>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 rounded-2xl bg-[#FF8A00] px-4 py-2.5 text-xs font-bold text-slate-950 hover:opacity-90"
          >
            <MessageCircle size={14} /> Envoyer via WhatsApp
          </a>
        </div>
      </div>

      {/* Contract modal */}
      {showContract && (
          <div className="mt-4 rounded-2xl border border-line bg-shell p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">Aperçu — Contrat de location</p>
              <button onClick={() => setShowContract(false)} className="rounded-full bg-shell p-2" aria-label="Fermer">
                <X size={14} />
              </button>
            </div>
            <div id="print-contract" className="mt-4 border border-line bg-card p-6 text-xs text-ink">
              <div className="border-b-2 border-ink pb-3 text-center">
                <p className="text-lg font-black tracking-tight">{agency?.name ?? 'fleetava Agadir'}</p>
                <p className="text-[11px] text-muted">
                  {agency ? `${agency.address} · ${agency.phone}` : 'Location de voitures — Agadir, Maroc · +212 6 61 00 00 00'}
                </p>
                {agency && (
                  <p className="text-[11px] text-muted">
                    {agency.rc} · {agency.ice} · {agency.patente}
                  </p>
                )}
                <p className="mt-2 inline-block max-w-full break-all rounded-full bg-ink px-3 py-1 text-[11px] font-bold text-shell" title={activeBooking.id}>
                  Contrat de location N° {activeBooking.id}
                </p>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <p className="font-bold uppercase text-muted">Client</p>
                  <p className="mt-1 truncate font-semibold" title={activeBooking.customerName}>{activeBooking.customerName}</p>
                  <p className="truncate" title={activeBooking.customerPhone}>Tél : {activeBooking.customerPhone}</p>
                  <p className="break-all" title={activeBooking.customerEmail}>Email : {activeBooking.customerEmail}</p>
                  <p className="truncate">Permis : {activeBooking.driverLicense || '—'}</p>
                  <p className="truncate">CIN / Passeport : {matchedClient?.cinPassport ?? '—'}</p>
                </div>
                <div className="min-w-0">
                  <p className="font-bold uppercase text-muted">Véhicule</p>
                  <p className="mt-1 truncate font-semibold" title={activeBooking.vehicleName}>{activeBooking.vehicleName}</p>
                  <p className="truncate" title={activeVehicle?.licensePlate ?? ''}>Matricule : {activeVehicle?.licensePlate ?? '—'}</p>
                  <p>Km sortie : {pickupKm || '—'} · Km retour : {returnKm || '—'}</p>
                  <p>Carburant sortie : {fuelLabel(pickupFuel)} · retour : {fuelLabel(returnFuel)}</p>
                </div>
              </div>
              <div className="mt-4">
                <p className="font-bold uppercase text-muted">Dates & lieux</p>
                <p className="mt-1">
                  Retrait : {activeBooking.pickupLocation} — {activeBooking.pickupDate} à {activeBooking.pickupTime}
                </p>
                <p>
                  Retour : {activeBooking.dropoffLocation} — {activeBooking.dropoffDate} à {activeBooking.dropoffTime} (
                  {activeBooking.rentalDays} j)
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="mt-4 w-full min-w-[420px] border border-line tabular-nums">
                <tbody>
                  <tr className="border-b border-line">
                    <td className="px-3 py-2">Total</td>
                    <td className="px-3 py-2 text-right font-bold">{formatMoney(activeBooking.totalPrice, cur)}</td>
                  </tr>
                  <tr className="border-b border-line">
                    <td className="px-3 py-2">Avance</td>
                    <td className="px-3 py-2 text-right">{formatMoney(activeBooking.avanceAmount ?? 0, cur)}</td>
                  </tr>
                  {charges.map((line, index) => (
                    <tr key={`${line.label}-${index}`} className="border-b border-line">
                      <td className="px-3 py-2">{line.label}</td>
                      <td className="px-3 py-2 text-right">{formatMoney(line.amount, cur)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-line">
                    <td className="px-3 py-2 font-bold">Reste à payer</td>
                    <td className="px-3 py-2 text-right font-bold">{formatMoney(reste, cur)}</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Caution ({activeBooking.cautionType ?? '—'})</td>
                    <td className="px-3 py-2 text-right">{formatMoney(activeBooking.cautionAmount ?? 0, cur)}</td>
                  </tr>
                </tbody>
                </table>
              </div>
              <p className="mt-3">
                État : Extérieur {checklist.exteriorClean ? 'propre ✓' : 'à vérifier'} · Documents{' '}
                {checklist.documentsHanded ? 'remis ✓' : 'non remis'} · Roue de secours{' '}
                {checklist.spareTire ? 'OK ✓' : 'manquante'} · Photos : {totalPhotos}
              </p>
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8">
                <div className="border-t border-line pt-2 text-center text-muted">Signature agence</div>
                <div className="border-t border-line pt-2 text-center text-muted">Signature client</div>
              </div>
              {agency?.contractFooter && (
                <p className="mt-4 text-center text-[10px] italic text-muted">{agency.contractFooter}</p>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setShowContract(false)}
                className="rounded-2xl border border-line px-4 py-2 text-xs font-bold"
              >
                Fermer
              </button>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 rounded-2xl bg-ink px-4 py-2 text-xs font-bold text-shell"
              >
                <Printer size={14} /> Imprimer (A4)
              </button>
            </div>
          </div>
      )}
      </SlideOver>
      )}
    </div>
  );
}

function PhotoSection({
  title,
  photos,
  multiple,
  onAdd,
  onRemove,
}: {
  title: string;
  photos: string[];
  multiple?: boolean;
  onAdd: (f: FileList | null) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-ink">{title} ({photos.length})</p>
        <label className="cursor-pointer rounded-xl border border-line bg-shell px-3 py-1.5 text-xs font-semibold text-ink hover:bg-shell">
          + Ajouter
          <input
            type="file"
            accept="image/*"
            multiple={multiple}
            className="hidden"
            onChange={(e) => {
              onAdd(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {photos.map((src, i) => (
            <div key={i} className="relative">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={src}
                alt={`${title} ${i + 1}`}
                className="h-20 w-28 rounded-xl border border-line object-cover"
              />
              <button
                onClick={() => onRemove(i)}
                className="absolute -right-2 -top-2 rounded-full bg-ink p-1 text-shell"
                aria-label="Supprimer photo"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
